import React, { useMemo } from "react";
import { TicketItem, useTicketState } from "../../../../contexts/ticket-state";
import sectionRegistry from "./sections";
import SortableList from "../../../theme/sortable/SortableList";
import Styles from "./TicketDetails.module.scss";
import TicketSectionPicker from "./TicketSectionPicker";
import { ChatSectionDisplayInput } from "../../../../__generated__/api";

interface TicketDetailsProps {
    ticket: TicketItem;
    isReadOnly: boolean;
    metricPathPrefix: string[];
    defaultSections: ChatSectionDisplayInput[] | {
        sectionId: string;
        isMinimized: boolean;
    }[];
}

export default function TicketDetails({
    ticket, isReadOnly, defaultSections, metricPathPrefix,
}: TicketDetailsProps) {
    const settings = useTicketState();
    const { sections, selectedSet } = useMemo(() => {
        const allSections = sectionRegistry.getAll();
        const sectionMap = allSections.toIdMap("sectionId");

        return {
            selectedSet: new Set(defaultSections.map(row => row.sectionId)),
            sections: defaultSections.mapFilter(section => {
                const sectionComponent = sectionMap[section.sectionId];
                if (!sectionComponent) {
                    return null;
                }
                return {
                    id: section.sectionId,
                    ...section,
                    Component: sectionComponent.component,
                };
            }),
        };
    }, [ticket, settings.userSettings]);

    return (
        <SortableList
            data-tour-panel="ticket-details"
            items={sections}
            onReorder={isReadOnly ? undefined : items => {
                settings.userSettings = {
                    ...settings.userSettings,
                    chatSections: items.map(item => ({
                        sectionId: item.id,
                        isMinimized: item.isMinimized,
                    })),
                };
            }}
            className={Styles.details}
            id="card-sections"
        >
            {sections.map(section => (
                <SortableList.Item
                    isEnabled={!isReadOnly}
                    shouldDragWholeElement={false}
                    id={section.id}
                    key={section.id}
                    item={section}
                >
                    <section.Component
                        ticket={ticket}
                        metricPathPrefix={metricPathPrefix}
                        isMinimized={section.isMinimized}
                        isReadOnly={isReadOnly}
                        onToggleExpand={({ isExpanded }) => {
                            if (!isReadOnly) {
                                settings.userSettings = {
                                    ...settings.userSettings,
                                    chatSections: sections.map(item => ({
                                        sectionId: item.id,
                                        isMinimized: item.id === section.id ? !isExpanded : item.isMinimized,
                                    })),
                                };
                            }
                        }}
                    />
                </SortableList.Item>
            ))}

            {
                !isReadOnly && (
                    <TicketSectionPicker metricPathPrefix={metricPathPrefix} sections={sections} selectedSet={selectedSet} />
                )
            }
        </SortableList>
    );
}
